import React, { useState } from 'react'
import { IInputProps } from '../../../types/interfaces/IInputProps'
import { PasswordStrength, PasswordInputContainer } from './style'
import { InputContainer, Input, Label } from '../input/style'
import { Error } from '../error/Error'
import { ShowPasswordButton } from '../show-password-button/style'
import { useSelector } from 'react-redux'
import { RootState, useAppSelector } from 'store/store'
import zxcvbn from 'zxcvbn'
import { useTranslation } from 'react-i18next'

interface IPasswordInputProps extends IInputProps {
  type?: 'new' | 'current'
}

export const PasswordInput: React.FC<IPasswordInputProps> = ({
  callback,
  hasError,
  errorText,
  className,
  disabled,
  labelText,
  type = 'current',
}) => {
  const [passwordType, setPasswordType] = useState<'password' | 'text'>(
    'password',
  )
  const isLoading = useAppSelector((state) => state.layout.isLoading)
  const { direction } = useSelector((state: RootState) => state.layout.language)
  const regex = /^(?=.*[A-Z])(?=.*\d)[A-Za-z\d!@#$%^&*]{8,}$/
  const [password, setPassword] = useState('')
  const [strength, setStrength] = useState(-1)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    const newPassword = e.target.value
    setPassword(newPassword)
    const error = !regex.test(newPassword)
    const evaluation = zxcvbn(newPassword)
    setStrength(evaluation.score)
    callback(newPassword, error)
  }

  const togglePasswordType = () => {
    setPasswordType((prevType) =>
      prevType === 'password' ? 'text' : 'password',
    )
  }

  const { t } = useTranslation('', {
    keyPrefix: 'components.password.strength',
  })

  const getStrengthLabel = () => {
    switch (strength) {
      case 0:
        return t('very-weak')
      case 1:
        return t('weak')
      case 2:
        return t('medium')
      case 3:
        return t('good')
      case 4:
        return t('strong')
      default:
        return ''
    }
  }

  return (
    <InputContainer>
      <PasswordInputContainer
        className={`password ${type === 'new' && `strength-${strength}`} ${
          className ?? ''
        } ${hasError ? 'error' : ''}`}
      >
        <Label htmlFor="password">{labelText}</Label>
        <Input
          type={passwordType}
          name="password"
          onChange={handleChange}
          autoComplete={`${type}-password`} // Enable browser password suggestions
          pattern={regex.toString().slice(1, -1)} // Convert regex to string and remove leading and trailing slashes
          disabled={disabled || isLoading}
        />
        <ShowPasswordButton
          direction={direction}
          onClick={togglePasswordType}
          type="button"
        >
          {passwordType === 'password' ? (
            <i className="password-eye"></i>
          ) : (
            <i className="password-eye-hide"></i>
          )}
        </ShowPasswordButton>
        {type === 'new' && !hasError && password.length > 7 && (
          <PasswordStrength
            direction={direction}
            className={`strength-${strength}`}
          >
            {getStrengthLabel()}
          </PasswordStrength>
        )}
      </PasswordInputContainer>
      <Error hasError={hasError} errorText={errorText} />
    </InputContainer>
  )
}
